// Fetch + cache for the official plugin index (StreamNook/streamnook-plugins).
// Shape per the signing spec's index document format; unknown fields ignored.

export interface PluginIndexAuthor {
  name: string;
  pubkey?: string;
  verified?: boolean;
}

export interface PluginIndexEntry {
  id: string;
  name: string;
  version: string;
  tier: 'A' | 'B' | 'C';
  description: string;
  homepage?: string;
  host_min?: string;
  released_at?: string;
  author: PluginIndexAuthor;
  icon_url?: string;
  banner_url?: string;
  readme_url?: string;
  downloads?: number;
  created_at?: string;
  updated_at?: string;
}

export interface PluginIndex {
  format: number;
  name: string;
  operator?: string;
  generated_at?: string;
  plugins: PluginIndexEntry[];
}

export const INDEX_REPO_URL = 'https://github.com/StreamNook/streamnook-plugins';
const INDEX_RAW_URL =
  'https://raw.githubusercontent.com/StreamNook/streamnook-plugins/main/index.json';

// Bumped to v2 alongside the index-repo move so any browser holding a cached
// empty index from the old personal repo refetches instead of waiting out the TTL.
const CACHE_KEY = 'streamnook_plugin_index_cache_v2';
const CACHE_TTL_MS = 30 * 60 * 1000;

interface Cache {
  fetched_at: number;
  index: PluginIndex;
}

function readCache(): PluginIndex | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const c = JSON.parse(raw) as Cache;
    if (Date.now() - c.fetched_at > CACHE_TTL_MS) return null;
    return c.index;
  } catch {
    return null;
  }
}

function writeCache(index: PluginIndex): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ fetched_at: Date.now(), index }));
  } catch {
    /* quota */
  }
}

export async function fetchPluginIndex(): Promise<PluginIndex> {
  const cached = readCache();
  if (cached) return cached;
  const r = await fetch(INDEX_RAW_URL, { headers: { accept: 'application/json' } });
  if (!r.ok) throw new Error(`index_${r.status}`);
  const index = (await r.json()) as PluginIndex;
  writeCache(index);
  return index;
}
